import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatDateTime } from "@/lib/format";
import {
  getMerosEinsatz,
  merosZustandLabel,
  speichereMerosKlappTermin,
  type MerosEinsatz,
  type MerosZustand,
} from "@/lib/meros";
import { toast } from "@/lib/toast";
import type { VeranstaltungEinsatz, VeranstaltungKontakt, VeranstaltungMail } from "@/lib/veranstaltungen";
import { MerosMitarbeiterWahl } from "@/pages/veranstaltungen/MerosMitarbeiterWahl";
import { MerosAbweichungBadge, MerosZustandBadge } from "@/pages/veranstaltungen/MerosZustandBadge";
import { RegieprotokollPdfDialog } from "@/pages/veranstaltungen/RegieprotokollPdfDialog";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";

function contactLine(contact: VeranstaltungKontakt | undefined): string {
  if (!contact) return "";
  return [contact.firma, contact.abteilung, contact.telefon, contact.mobil, contact.email]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(" · ");
}

function InfoZeile({ label, value }: { label: string; value?: string | null }) {
  const text = (value || "").trim();
  if (!text) return null;
  return (
    <p className="text-sm">
      <span className="text-label-secondary">{label} </span>
      <span className="whitespace-pre-line text-label">{text}</span>
    </p>
  );
}

function EinsatzBlock({ einsatz }: { einsatz: VeranstaltungEinsatz }) {
  return (
    <div className="rounded-ios bg-fill-quaternary/25 px-3 py-2 text-sm">
      <p className="font-medium text-label">
        {[einsatz.bereich, einsatz.ort].filter(Boolean).join(" · ") || "Einsatz"}
      </p>
      <p className="mt-1 text-label-secondary">
        {[einsatz.datum, einsatz.kategorie, einsatz.abteilung, einsatz.firma].filter(Boolean).join(" · ")}
      </p>
      {einsatz.aufgabe ? <p className="mt-1 text-label">{einsatz.aufgabe}</p> : null}
      {einsatz.notiz ? (
        <p className="mt-1 whitespace-pre-line text-label-secondary">{einsatz.notiz}</p>
      ) : null}
    </div>
  );
}

function VeranstaltungInhalt({ data }: { data: Extract<MerosEinsatz, { art: "veranstaltung" }> }) {
  const [pdf, setPdf] = useState<{ id: string; name: string } | null>(null);
  const mail: VeranstaltungMail = data.mail;
  const details = mail.details;
  const projekt = details?.projekt;
  const partner = details?.ansprechpartner;
  const veranstalter = details?.veranstalter;
  const leiter = (details?.kontakte || []).find((item) => item.rolle === "Veranstaltungsleiter" && item.name);
  const protokoll = (mail.attachments_summary || []).filter((att) => att.is_rp);
  const highlighted = data.einsatz;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-label-secondary">
          {[mail.event_date, mail.venue, mail.event_version ? `Version ${mail.event_version}` : ""]
            .filter(Boolean)
            .join(" · ") || "Ohne Datum"}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {highlighted?.zustand ? <MerosZustandBadge zustand={highlighted.zustand} /> : null}
          {highlighted?.abweichung ? (
            <MerosAbweichungBadge ist={highlighted.ist_zustand} />
          ) : null}
          {mail.rp_nummer ? (
            <span className="text-xs font-medium text-label-secondary">RP {mail.rp_nummer}</span>
          ) : null}
          {mail.psp ? <span className="text-xs font-medium text-label-secondary">PSP {mail.psp}</span> : null}
        </div>
      </div>

      {highlighted ? (
        <section className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
            Merostützen in diesem Einsatz
          </p>
          <InfoZeile label="Wann" value={highlighted.datum || highlighted.von || undefined} />
          <InfoZeile
            label="Wo"
            value={[highlighted.bereich, highlighted.ort, highlighted.venue].filter(Boolean).join(" · ")}
          />
          <InfoZeile label="Aufgabe" value={highlighted.aufgabe} />
          <InfoZeile label="Notiz" value={highlighted.notiz} />
          <InfoZeile
            label="Soll"
            value={highlighted.zustand ? merosZustandLabel(highlighted.zustand) : "Richtung unklar"}
          />
        </section>
      ) : null}

      <section className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Veranstaltung</p>
        <InfoZeile label="Absender" value={[mail.from_name, mail.from_email].filter(Boolean).join(" · ")} />
        <InfoZeile label="Betreff" value={mail.subject} />
        <InfoZeile label="Kunde" value={[details?.kunde, details?.kundennummer].filter(Boolean).join(" · ")} />
        <InfoZeile label="Angebot" value={details?.angebot} />
        <InfoZeile label="Ort" value={details?.ort} />
        <InfoZeile
          label="Ansprechpartner"
          value={[partner?.name, contactLine(partner)].filter(Boolean).join(" · ")}
        />
        <InfoZeile
          label="Veranstalter"
          value={[veranstalter?.name || veranstalter?.firma, contactLine(veranstalter)].filter(Boolean).join(" · ")}
        />
        <InfoZeile label="Veranstaltungsleiter" value={[leiter?.name, contactLine(leiter)].filter(Boolean).join(" · ")} />
        <InfoZeile label="Beschreibung" value={projekt?.beschreibung} />
        <InfoZeile label="Projektnotiz" value={projekt?.notiz} />
      </section>

      {mail.einsaetze.length > 0 ? (
        <section className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
            Einsätze aus dem Regieprotokoll
          </p>
          {mail.einsaetze.map((einsatz, index) => (
            <EinsatzBlock key={`${einsatz.datum}-${index}`} einsatz={einsatz} />
          ))}
        </section>
      ) : null}

      <section className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Regieprotokoll</p>
        {protokoll.length === 0 ? (
          <p className="text-sm text-label-secondary">Kein Regieprotokoll in dieser Mail.</p>
        ) : data.can_edit ? (
          <div className="flex flex-wrap gap-2">
            {protokoll.map((att) => (
              <Button
                key={att.id}
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setPdf({ id: att.id, name: att.name || "Regieprotokoll" })}
              >
                {att.name || "Regieprotokoll"}
              </Button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-label-secondary">
            {protokoll.map((att) => att.name || "Regieprotokoll").join(", ")}
          </p>
        )}
      </section>

      <RegieprotokollPdfDialog
        open={pdf != null}
        mailId={mail.id}
        attachmentId={pdf?.id ?? null}
        title={pdf?.name || "Regieprotokoll"}
        zIndexClass="z-[220]"
        onClose={() => setPdf(null)}
      />
    </div>
  );
}

function zustandText(zustand: MerosZustand | null | undefined): string {
  return zustand ? merosZustandLabel(zustand) : "unklar";
}

function KlappInhalt({
  data,
  onSaved,
}: {
  data: Extract<MerosEinsatz, { art: "klapp" }>;
  onSaved: () => void;
}) {
  const termin = data.termin;
  const [notiz, setNotiz] = useState(termin.notiz || "");
  const [selected, setSelected] = useState((termin.mitarbeiter || []).map((row) => row.row_id));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNotiz(termin.notiz || "");
    setSelected((termin.mitarbeiter || []).map((row) => row.row_id));
  }, [termin]);

  async function save() {
    setSaving(true);
    try {
      await speichereMerosKlappTermin(termin.id, {
        notiz: notiz.trim(),
        mitarbeiter_row_ids: selected,
      });
      toast.success("Klapp-Termin gespeichert.");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Der Termin konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  const namen = (termin.mitarbeiter || []).map((row) => row.label).filter(Boolean);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-label">{formatDateTime(termin.klapp_am)}</p>
        <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-label-secondary">
          <MerosZustandBadge zustand={termin.zustand_davor} prefix="" />
          <span aria-hidden>→</span>
          <MerosZustandBadge zustand={termin.zustand_danach} prefix="" />
          {!termin.zustand_davor || !termin.zustand_danach ? (
            <span>
              {zustandText(termin.zustand_davor)} → {zustandText(termin.zustand_danach)}
            </span>
          ) : null}
        </p>
        {termin.erfasst_von ? (
          <p className="mt-1 text-xs text-label-tertiary">Eingetragen von {termin.erfasst_von}</p>
        ) : null}
      </div>

      {data.can_edit ? (
        <>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-label-secondary">Notiz</span>
            <textarea
              className="eh-input min-h-[5rem]"
              maxLength={500}
              value={notiz}
              onChange={(event) => setNotiz(event.target.value)}
            />
          </label>
          <div className="space-y-2">
            <p className="text-sm text-label-secondary">Mitarbeiter im Montageplan</p>
            <MerosMitarbeiterWahl
              options={data.mitarbeiter}
              selected={selected}
              disabled={saving}
              onChange={setSelected}
            />
          </div>
          <div className="flex justify-end">
            <Button type="button" onClick={() => void save()} loading={saving}>
              Speichern
            </Button>
          </div>
        </>
      ) : (
        <>
          {termin.notiz ? <p className="whitespace-pre-line text-sm text-label">{termin.notiz}</p> : null}
          <p className="text-sm text-label-secondary">
            {namen.length > 0 ? namen.join(", ") : "Keine Mitarbeiter zugeordnet."}
          </p>
        </>
      )}
    </div>
  );
}

export function MerosEinsatzDialog({
  assignmentId,
  onClose,
  onSaved,
}: {
  assignmentId: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [data, setData] = useState<MerosEinsatz | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (assignmentId == null) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getMerosEinsatz(assignmentId)
      .then((next) => {
        if (!cancelled) setData(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setData(null);
        setError(err instanceof ApiError ? err.message : "Die Angaben konnten nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assignmentId]);

  return (
    <Dialog
      open={assignmentId != null}
      onClose={onClose}
      size={data?.art === "veranstaltung" ? "lg" : "md"}
      zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
      title={
        data?.art === "klapp"
          ? "Klappen"
          : data?.art === "veranstaltung"
            ? data.mail.event_title || data.mail.subject || "Veranstaltung"
            : "Einsatz"
      }
      bodyClassName="px-4 py-4 sm:px-5"
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner className="h-6 w-6" />
        </div>
      ) : error ? (
        <p className="text-sm text-status-red">{error}</p>
      ) : data?.art === "veranstaltung" ? (
        <VeranstaltungInhalt data={data} />
      ) : data?.art === "klapp" ? (
        <KlappInhalt
          data={data}
          onSaved={() => {
            onSaved();
            onClose();
          }}
        />
      ) : null}
    </Dialog>
  );
}
